import { type CSSProperties, useState, useEffect, useMemo } from "react";
import { type TeamColorProps } from "./types";
import { useTeamColors } from "shared/src/hooks/useTeamColors";
import omit from "lodash-es/omit";
import pick from "lodash-es/pick";
import { Background } from "components/src/components/Background/Background";
import { getCompetitionInfo } from "shared/src/hooks/getCompetitionInfo";

const defaultProps = {
  colorMode: "primary",
  colorType: "normal",
  gradient: "horizontal",
  gradientSharpness: "blurry",
  width: "100%" as CSSProperties["width"],
  height: "auto" as CSSProperties["width"],
  fallbackPrimaryColor: "bg-primary-strong",
  fallbackSecondaryColor: "gray-dark-100",
  position: "relative",
};

export const TeamColor = ({
  style = {},
  className = "",
  wrapperClasses = "",
  competition,
  league,
  children,
  render,
  getColors,
  ...rest
}: TeamColorProps) => {
  // Support deprecated 'league' prop
  const _competition = competition || league;
  // Need to extract team info this way or Typescript complains because of discriminated union in TeamColorProps;
  const { team, team1, team2 } = pick(rest, ["team", "team1", "team2"]);
  
  const hasTwoTeams = (!!team || !!team1) && !!team2;
  const _teamColorMapping = hasTwoTeams ? "dual" : "single";

  const _team1 = team || team1;

  let teamColors = {
    team1: typeof _team1 === "object" && "name" in _team1 ? useTeamColors(_team1.name) : useTeamColors(_team1),
    team2: typeof team2 === "object" && "name" in team2 ? useTeamColors(team2.name) : useTeamColors(team2),
  };

  let competitionColors = typeof _competition === "string" && _competition.toLowerCase() === "live" ? {
      name: "Live",
      metadata: {
        colors: {
          primary: {value: "var(--highlight-accent-strong)"},
          secondary: {value: "var(--ultramarine-1000)"}
        }
      }
  } : getCompetitionInfo(_competition?.toLowerCase());

  const htmlAttrs = omit(rest, ["team", "team1", "team2", "teamColorVariant", "colorMode", "colorType", "gradient", "gradientSharpness", "width", "height", "fallbackPrimaryColor", "fallbackSecondaryColor", "style", "className", "wrapperClasses", "children", "position", "render", "getColors"]);

  const _colorMode = useMemo(() => {
    if (_teamColorMapping === "dual") return "both";
    return rest?.colorMode;
  }, [rest?.colorMode, _teamColorMapping]);

  const _teamColorVariant = useMemo(()=>{
    return rest?.teamColorVariant ?? "primary";
  },[rest?.teamColorVariant]);

  const _props = useMemo(() => {
    const _rest = omit(rest, ["teamColorVariant"]);
    return {
      ...defaultProps,
      ..._rest,
      colorMode: _colorMode
    };
  }, [_colorMode, _teamColorMapping, teamColors]);

  const _colors = useMemo(() => {
    let primaryColor = teamColors.team1?.colors[0];
    let secondaryColor = teamColors.team1?.colors[1];
    if (_teamColorMapping === "single") {
      primaryColor = teamColors.team1?.colors[0];
      secondaryColor = teamColors.team1?.colors[1];
    }
    if (_teamColorMapping === "dual") {
      primaryColor =
        _teamColorVariant === "primary"
          ? teamColors.team1?.colors[0] ?? _props.fallbackPrimaryColor
          : _teamColorVariant === "secondary"  
          ? teamColors.team1?.colors[1] ?? _props.fallbackPrimaryColor
          : _props.fallbackPrimaryColor
      secondaryColor =
        _teamColorVariant === "primary"
          ? teamColors.team2?.colors[0] ?? _props.fallbackSecondaryColor
          : _teamColorVariant === "secondary" 
          ? teamColors.team2?.colors[1] ?? _props.fallbackSecondaryColor
          : _props.fallbackSecondaryColor
    }
    
    if (_competition && !(_team1 || team2)) {
        primaryColor = competitionColors?.metadata?.colors?.primary?.value;
        secondaryColor = competitionColors?.metadata?.colors?.secondary?.value;
    }

    return {
      primaryColor,
      secondaryColor,
    };
  }, [
    teamColors?.team1,
    teamColors?.team2,
    _colorMode,
    _teamColorVariant,
    _teamColorMapping,
    _competition
  ]);
  
  return (
    <Background
      wrapperClasses={`cui__team-color ${wrapperClasses}`.trim()}
      {..._props}
      {..._colors}
      {...htmlAttrs}
    >{children}</Background>
  );
};
